// خدمة إرجاع الصيانة
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  MaintenanceReturn,
  MaintenanceReturnReason,
  MaintenanceStatus,
  MaintenanceRecord
} from '../utils/types';
import { getMaintenanceById, updateMaintenanceStatus } from './maintenanceService';
import { updateInventoryQuantityInWarehouse, addInventoryTransaction } from './inventoryService';
import { TransactionType } from '../utils/types';

/**
 * إنشاء إرجاع صيانة جديد
 */
export const createMaintenanceReturn = async (
  returnData: {
    maintenanceId: string;
    returnReason: MaintenanceReturnReason;
    customReturnReason?: string;
    issueDescription: string;
    refundAmount: number;
    refundMethod: 'cash' | 'debt_reduction';
    deviceReturned: boolean;
    notes?: string;
    employeeId: string;
    employeeName: string;
  }
): Promise<MaintenanceReturn> => {
  try {
    console.log('🔄 بدء إنشاء إرجاع صيانة:', returnData.maintenanceId);

    // الحصول على بيانات الصيانة
    const maintenance = await getMaintenanceById(returnData.maintenanceId);
    if (!maintenance) {
      throw new Error(`طلب الصيانة غير موجود: ${returnData.maintenanceId}`);
    }

    // التحقق من حالة الصيانة
    if (maintenance.status === MaintenanceStatus.RETURNED) {
      throw new Error('هذا الطلب مرتجع بالفعل');
    }

    if (maintenance.status === MaintenanceStatus.DELIVERED) {
      throw new Error('لا يمكن إرجاع طلب تم تسليمه بالفعل');
    }

    // إنشاء سجل الإرجاع
    const returnRecord: Omit<MaintenanceReturn, 'id'> = {
      maintenanceId: returnData.maintenanceId,
      returnReason: returnData.returnReason,
      customReturnReason: returnData.customReturnReason,
      issueDescription: returnData.issueDescription,
      refundAmount: returnData.refundAmount,
      refundMethod: returnData.refundMethod,
      deviceReturned: returnData.deviceReturned,
      notes: returnData.notes,
      employeeId: returnData.employeeId,
      employeeName: returnData.employeeName,
      customerId: maintenance.customerId,
      customerName: maintenance.customerName,
      deviceType: maintenance.deviceType,
      deviceBrand: (maintenance as any).brand,
      deviceModel: (maintenance as any).model,
      createdAt: new Date(),
      processedAt: new Date()
    };

    // حفظ سجل الإرجاع
    const returnsCollection = collection(db, 'maintenanceReturns');
    const returnDocRef = await addDoc(returnsCollection, {
      ...returnRecord,
      createdAt: serverTimestamp(),
      processedAt: serverTimestamp()
    });

    console.log('✅ تم إنشاء سجل الإرجاع:', returnDocRef.id);

    // تحديث حالة الصيانة إلى "مرتجع"
    await updateMaintenanceStatus(returnData.maintenanceId, MaintenanceStatus.RETURNED);
    console.log('✅ تم تحديث حالة الصيانة إلى مرتجع');

    // معالجة الاسترداد المالي
    if (returnData.refundAmount > 0) {
      await processRefund(returnData.maintenanceId, returnData.refundAmount, returnData.refundMethod, returnData.employeeId);
    }

    // إضافة نشاط للصيانة
    await addMaintenanceActivity(
      returnData.maintenanceId,
      'return_created',
      `تم إرجاع الجهاز - السبب: ${getReturnReasonLabel(returnData.returnReason)}`,
      returnData.employeeId,
      returnData.employeeName
    );

    return {
      id: returnDocRef.id,
      ...returnRecord
    };

  } catch (error) {
    console.error('❌ خطأ في إنشاء إرجاع الصيانة:', error);
    throw error;
  }
};

/**
 * معالجة الاسترداد المالي
 */
const processRefund = async (
  maintenanceId: string,
  refundAmount: number,
  refundMethod: 'cash' | 'debt_reduction',
  employeeId: string
): Promise<void> => {
  try {
    console.log(`💰 معالجة الاسترداد: ${refundAmount} ج.م بطريقة ${refundMethod}`);

    if (refundMethod === 'cash') {
      // خصم من الخزينة
      await addCashDrawerTransaction({
        type: 'maintenance_refund',
        amount: -Math.abs(refundAmount), // سالب لأنه خصم من الخزينة
        description: `استرداد نقدي لإرجاع صيانة - ${maintenanceId.substring(0, 8)}`,
        maintenanceId,
        employeeId,
        date: new Date().toISOString().split('T')[0],
        createdAt: serverTimestamp()
      });
      console.log('✅ تم خصم المبلغ من الخزينة');
    } else if (refundMethod === 'debt_reduction') {
      // تقليل الدين على العميل
      const maintenance = await getMaintenanceById(maintenanceId);
      if (maintenance?.customerId) {
        await reduceCustomerDebt(maintenance.customerId, refundAmount, maintenanceId);
        console.log('✅ تم تقليل دين العميل');
      }
    }

  } catch (error) {
    console.error('❌ خطأ في معالجة الاسترداد:', error);
    throw error;
  }
};

/**
 * إضافة معاملة للخزينة
 */
const addCashDrawerTransaction = async (transactionData: any): Promise<void> => {
  const drawerCollection = collection(db, 'drawerTransactions');
  await addDoc(drawerCollection, transactionData);
};

/**
 * تقليل دين العميل
 */
const reduceCustomerDebt = async (customerId: string, amount: number, maintenanceId: string): Promise<void> => {
  // هذه الدالة تحتاج تنفيذ حسب نظام إدارة الديون
  console.log(`تقليل دين العميل ${customerId} بمبلغ ${amount} للصيانة ${maintenanceId}`);
};

/**
 * إضافة نشاط للصيانة
 */
const addMaintenanceActivity = async (
  maintenanceId: string,
  activityType: string,
  description: string,
  employeeId: string,
  employeeName: string
): Promise<void> => {
  const activitiesCollection = collection(db, 'maintenanceActivities');
  await addDoc(activitiesCollection, {
    maintenanceId,
    type: activityType,
    description,
    employeeId,
    employeeName,
    createdAt: serverTimestamp()
  });
};

/**
 * الحصول على جميع إرجاعات الصيانة
 */
export const getMaintenanceReturns = async (maintenanceId?: string): Promise<MaintenanceReturn[]> => {
  try {
    const returnsCollection = collection(db, 'maintenanceReturns');

    let q = query(returnsCollection, orderBy('createdAt', 'desc'));

    if (maintenanceId) {
      q = query(returnsCollection, where('maintenanceId', '==', maintenanceId), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    const returns: MaintenanceReturn[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      returns.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        processedAt: data.processedAt?.toDate() || new Date()
      } as MaintenanceReturn);
    });

    return returns;

  } catch (error) {
    console.error('❌ خطأ في جلب إرجاعات الصيانة:', error);
    throw error;
  }
};

/**
 * الحصول على تسمية سبب الإرجاع
 */
const getReturnReasonLabel = (reason: MaintenanceReturnReason): string => {
  const labels: Record<MaintenanceReturnReason, string> = {
    [MaintenanceReturnReason.SAME_ISSUE]: 'نفس العيب',
    [MaintenanceReturnReason.NEW_ISSUE]: 'عيب جديد',
    [MaintenanceReturnReason.CUSTOMER_REFUSAL]: 'رفض العميل للإصلاح',
    [MaintenanceReturnReason.COST_TOO_HIGH]: 'التكلفة عالية',
    [MaintenanceReturnReason.PART_NOT_AVAILABLE]: 'قطعة غير متوفرة',
    [MaintenanceReturnReason.DEVICE_BEYOND_REPAIR]: 'الجهاز غير قابل للإصلاح',
    [MaintenanceReturnReason.OTHER]: 'أخرى'
  };

  return labels[reason] || reason;
};

/**
 * إحصائيات الإرجاعات
 */
export const getReturnStats = async (startDate?: Date, endDate?: Date): Promise<{
  totalReturns: number;
  totalRefundAmount: number;
  returnsByReason: Record<MaintenanceReturnReason, number>;
  returnsByMethod: Record<string, number>;
}> => {
  try {
    const returnsCollection = collection(db, 'maintenanceReturns');

    let q = query(returnsCollection);

    if (startDate && endDate) {
      q = query(
        returnsCollection,
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate))
      );
    }

    const querySnapshot = await getDocs(q);

    let totalReturns = 0;
    let totalRefundAmount = 0;
    const returnsByReason: Record<MaintenanceReturnReason, number> = {} as any;
    const returnsByMethod: Record<string, number> = {};

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      totalReturns++;
      totalRefundAmount += data.refundAmount || 0;

      // تجميع حسب السبب
      const reason = data.returnReason as MaintenanceReturnReason;
      returnsByReason[reason] = (returnsByReason[reason] || 0) + 1;

      // تجميع حسب طريقة الاسترداد
      const method = data.refundMethod;
      returnsByMethod[method] = (returnsByMethod[method] || 0) + 1;
    });

    return {
      totalReturns,
      totalRefundAmount,
      returnsByReason,
      returnsByMethod
    };

  } catch (error) {
    console.error('❌ خطأ في جلب إحصائيات الإرجاعات:', error);
    throw error;
  }
};


/**
 * إرجاع قطع غيار محددة من طلب صيانة (إرجاع جزئي - محسّن)
 * يعيد القطع المحددة للمخزون ويخصم قيمتها من إجمالي الصيانة
 */
export const returnMaintenanceParts = async (
  maintenanceId: string,
  partsToReturn: Array<{
    partId: string;
    quantity: number;
    reason: string;
  }>,
  refundMethod: 'cash' | 'debt_reduction',
  employeeId: string,
  employeeName: string,
  notes?: string
): Promise<void> => {
  try {
    // جلب بيانات الصيانة
    const maintenance = await getMaintenanceById(maintenanceId);
    if (!maintenance) {
      throw new Error('طلب الصيانة غير موجود');
    }

    const usedParts = maintenance.usedParts || [];
    const warehouseId = maintenance.warehouseId;

    if (!warehouseId) {
      throw new Error('لا يوجد معرف مخزن للصيانة');
    }

    let totalRefundAmount = 0;
    const returnedParts: any[] = [];
    const partsToUpdate: any[] = [];

    // التحقق من جميع القطع أولاً
    for (const returnItem of partsToReturn) {
      const partIndex = usedParts.findIndex(
        (p: any) => (p.id || p.itemId) === returnItem.partId
      );

      if (partIndex === -1) {
        throw new Error(`القطعة ${returnItem.partId} غير موجودة`);
      }

      const part: any = usedParts[partIndex];
      const availableQuantity = part.quantity || 0;

      if (returnItem.quantity > availableQuantity) {
        throw new Error(
          `كمية غير صحيحة للقطعة ${part.name || 'غير معروفة'}`
        );
      }

      const partId = part.id || part.itemId;
      const partName = part.name || 'قطعة غير معروفة';
      const sellingPrice = part.sellingPrice || 0;
      const refundAmount = sellingPrice * returnItem.quantity;

      totalRefundAmount += refundAmount;

      partsToUpdate.push({
        partId,
        partName,
        partIndex,
        quantity: returnItem.quantity,
        availableQuantity,
        refundAmount,
        reason: returnItem.reason
      });
    }

    // ✅ إعادة جميع القطع للمخزون بشكل متوازي (محسّن)
    await Promise.all(
      partsToUpdate.map(async (partData: any) => {
        await updateInventoryQuantityInWarehouse(
          partData.partId,
          warehouseId,
          partData.quantity,
          employeeId,
          `إرجاع جزئي صيانة #${maintenanceId}`
        );

        return addInventoryTransaction({
          id: '',
          type: TransactionType.ADJUSTMENT,
          itemId: partData.partId,
          itemName: partData.partName,
          itemSku: '',
          quantity: partData.quantity,
          toWarehouseId: warehouseId,
          toWarehouseName: 'مخزن الصيانة',
          createdById: employeeId,
          createdByName: employeeName,
          notes: `إرجاع جزئي - ${partData.reason}`,
          timestamp: new Date()
        });
      })
    );

    // تحديث قائمة القطع المستخدمة
    for (const partData of partsToUpdate) {
      if (partData.quantity === partData.availableQuantity) {
        usedParts.splice(partData.partIndex, 1);
      } else {
        usedParts[partData.partIndex].quantity -= partData.quantity;
      }

      returnedParts.push({
        partId: partData.partId,
        partName: partData.partName,
        quantity: partData.quantity,
        refundAmount: partData.refundAmount,
        reason: partData.reason
      });
    }

    // تحديث تكلفة قطع الغيار
    const newPartsCost = usedParts.reduce((total: number, part: any) => {
      return total + (part.sellingPrice || 0) * (part.quantity || 0);
    }, 0);

    // تحديث الإجمالي
    const newTotal = (maintenance.laborCost || 0) + newPartsCost;

    // تحديث طلب الصيانة
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    await updateDoc(maintenanceRef, {
      usedParts,
      partsCost: newPartsCost,
      total: newTotal,
      updatedAt: new Date()
    });

    // معالجة الاسترداد المالي
    if (totalRefundAmount > 0) {
      if (refundMethod === 'cash') {
        // خصم من الخزينة
        console.log('💰 خصم المبلغ من الخزينة...');

        await addDoc(collection(db, 'drawerTransactions'), {
          type: 'maintenance_partial_refund',
          amount: -Math.abs(totalRefundAmount),
          description: `إرجاع جزئي لقطع غيار - صيانة ${maintenanceId}`,
          maintenanceId,
          employeeId,
          employeeName,
          branchId: maintenance.branchId || 'default',
          date: new Date().toISOString().split('T')[0],
          createdAt: serverTimestamp()
        });

        console.log(`✅ تم خصم ${totalRefundAmount} جنيه من الخزينة`);
      }
    }

    // تسجيل الإرجاع الجزئي
    await addDoc(collection(db, 'maintenancePartialReturns'), {
      maintenanceId,
      returnedParts,
      totalRefundAmount,
      refundMethod,
      employeeId,
      employeeName,
      notes: notes || '',
      createdAt: serverTimestamp()
    });

    console.log(`✅ تم إرجاع ${partsToReturn.length} قطعة غيار بنجاح`);
  } catch (error) {
    console.error('❌ خطأ في إرجاع قطع الغيار:', error);
    throw error;
  }
};
